import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "container" }
const _hoisted_2 = {
  key: 0,
  class: "player__content"
}
const _hoisted_3 = { class: "player__control" }
const _hoisted_4 = { class: "player-play" }
const _hoisted_5 = ["disabled"]
const _hoisted_6 = ["disabled"]
const _hoisted_7 = ["disabled"]
const _hoisted_8 = ["disabled"]
const _hoisted_9 = { class: "player__control-m" }
const _hoisted_10 = {
  key: 1,
  class: "player--loading"
}

import { computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import CircleSpinner from '@/components/ui/CircleSpinner.vue'

import { playerSession } from '@/services/player'
import { assertNotNull } from '@/helpers/typing'
import { miniPlayer19040Variant } from '@/services/ab'

import PlayerChapterProgress from './ChapterProgress.vue'
import PlayerVolume from './Volume.vue'
import PlayerRate from './Rate.vue'
import type { DocView } from '@/models/doc'


export default /*@__PURE__*/_defineComponent({
  __name: 'Player',
  props: {
    doc: {}
  },
  setup(__props: any) {

const props = __props

const router = useRouter()
const route = useRoute()

// The audio element and the controls belong to the global player session, so
// that the playback survives switching to the Mini Player, see #19040.
const playbackControl = computed(() => playerSession.value.playbackControl)
const chapterControl = computed(() => playerSession.value.chapterControl)
const progressControl = computed(() => playerSession.value.progressControl)
const rateControl = computed(() => playerSession.value.rateControl)
const volumeControl = computed(() => playerSession.value.volumeControl)

onMounted(async () => {
  await playerSession.value.start(
    props.doc,
    // Follow the playback with the reading page: when the audio reaches the
    // next chapter, navigate to it.
    (chapterIdx: number) => {
      router.push(props.doc.pageLink(chapterIdx).params)
    },
    props.doc.pageNum,
  )

  // Hook chapter control to doc.pageNum changes, so when we navigate
  // the text chapters, the playback follows.
  watch(
    () => props.doc.pageNum,
    (newPageNum: number) => {
      assertNotNull(chapterControl.value).go(newPageNum)
    },
  )
})

onUnmounted(async () => {
  if (miniPlayer19040Variant() === 'B') {
    // Keep the player session alive, so that it can be taken over by the Mini Player,
    // but remove the chapter change callback (it updates the current route,
    // which we don't want in the Mini Player).
    playerSession.value.setChapterChangeCallback(() => {})
  } else {
    // If there's no Mini Player, we just stop the player session.
    await playerSession.value.stop()
  }
})

watch(
  () => playerSession.value.isPlaying,
  (isPlaying: boolean) => {
    props.doc.isAudioPlaying = isPlaying
  },
  { immediate: true },
)

const isBook = computed(() => {
  return props.doc.doc_type === 'book'
})

function togglePlayback(): void {
  playerSession.value.togglePlayback({
    entryPoint: 'player',
    referrerPageName: route.fullPath,
  })
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["player", __props.doc.controls.playerClass])
  }, [
    _createElementVNode("div", _hoisted_1, [
      (
          playbackControl.value &&
          chapterControl.value &&
          progressControl.value &&
          rateControl.value &&
          volumeControl.value
        )
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              _createVNode(PlayerVolume, {
                volumeControl: volumeControl.value,
                class: "player-volume--pc"
              }, null, 8, ["volumeControl"]),
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("button", {
                  class: "player-play__link player-play__link--backward",
                  disabled: !playbackControl.value.audioLoaded,
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (playbackControl.value.backward()))
                }, [...(_cache[5] || (_cache[5] = [
                  _createElementVNode("span", { class: "iconfont iplayer-reduce" }, null, -1)
                ]))], 8, _hoisted_5),
                (isBook.value)
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 0,
                      class: "player-play__link player-play__link--prev",
                      disabled: !chapterControl.value.hasPrev(),
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (chapterControl.value.prev()))
                    }, [...(_cache[6] || (_cache[6] = [
                      _createElementVNode("span", { class: "iconfont iplayer-prev" }, null, -1)
                    ]))], 8, _hoisted_6))
                  : _createCommentVNode("", true),
                _createElementVNode("button", {
                  class: "player-play__link player-play__link--play",
                  onClick: _cache[2] || (_cache[2] = ($event: any) => (togglePlayback()))
                }, [
                  (
                  !playbackControl.value.paused &&
                  playbackControl.value.interruptedByNetwork
                )
                    ? (_openBlock(), _createBlock(CircleSpinner, {
                        key: 0,
                        "animation-duration": 2000,
                        size: 58,
                        color: '#ffc906'
                      }))
                    : (_openBlock(), _createElementBlock("span", {
                        key: 1,
                        class: _normalizeClass(["iconfont", {
                  'iplayer-pause': !playbackControl.value.paused,
                  'iplayer-play': playbackControl.value.paused,
                }])
                      }, null, 2))
                ]),
                (isBook.value)
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 1,
                      class: "player-play__link player-play__link--next",
                      disabled: !chapterControl.value.hasNext(),
                      onClick: _cache[3] || (_cache[3] = ($event: any) => (chapterControl.value.next()))
                    }, [...(_cache[7] || (_cache[7] = [
                      _createElementVNode("span", { class: "iconfont iplayer-next" }, null, -1)
                    ]))], 8, _hoisted_7))
                  : _createCommentVNode("", true),
                _createElementVNode("button", {
                  class: "player-play__link player-play__link--forward",
                  disabled: !playbackControl.value.audioLoaded,
                  onClick: _cache[4] || (_cache[4] = ($event: any) => (playbackControl.value.forward()))
                }, [...(_cache[8] || (_cache[8] = [
                  _createElementVNode("span", { class: "iconfont iplayer-add" }, null, -1)
                ]))], 8, _hoisted_8)
              ]),
              _createVNode(PlayerRate, {
                rateControl: rateControl.value,
                class: "player-speed--pc"
              }, null, 8, ["rateControl"])
            ]),
            _createVNode(PlayerChapterProgress, {
              progressControl: progressControl.value,
              chapterControl: chapterControl.value
            }, null, 8, ["progressControl", "chapterControl"]),
            _createElementVNode("div", _hoisted_9, [
              _createVNode(PlayerVolume, { volumeControl: volumeControl.value }, null, 8, ["volumeControl"]),
              _createVNode(PlayerRate, { rateControl: rateControl.value }, null, 8, ["rateControl"])
            ])
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_10, [
            _createVNode(CircleSpinner, {
              "animation-duration": 2000,
              size: 36,
              color: '#ffc906'
            })
          ]))
    ])
  ], 2))
}
}

})